import { Moment } from 'moment-timezone'
import React from 'react'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { toDollars } from 'src/utils/currency.utils'
import { convertUTCTimezone } from 'src/utils/date.utils'

export enum RecurrencePeriod {
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  Yearly = 'YEARLY',
}

export enum RecurrenceTerminator {
  None = 'NONE',
  Count = 'COUNT',
  Date = 'DATE',
  CountOrDate = 'COUNT_OR_DATE',
}

export interface RecurrenceResponse {
  id?: string
  balanceCents: number
  period: RecurrencePeriod
  interval: number
  terminator: RecurrenceTerminator
  count: number
  until?: string
  byWeekDay?: number
  byMonthDay?: number
  byYearDay?: number
  prevRecurrenceAt?: string
  nextRecurrenceAt?: string
  currentCount: number
  remainingCount?: number
  createdAt?: string
  updatedAt?: string
}

export class RecurrenceModel {
  public id?: string
  public balanceCents: number
  public period: RecurrencePeriod
  public interval: number
  public terminator: RecurrenceTerminator
  public count: number
  public until?: string
  public byWeekDay?: number
  public byMonthDay?: number
  public byYearDay?: number
  public prevRecurrenceAt?: string
  public nextRecurrenceAt?: string
  public currentCount: number
  public remainingCount?: number
  public createdAt?: string
  public updatedAt?: string

  public constructor(data: RecurrenceResponse) {
    this.id = data.id
    this.balanceCents = data.balanceCents
    this.period = data.period
    this.interval = data.interval
    this.terminator = data.terminator
    this.count = data.count
    this.until = data.until
    this.byWeekDay = data.byWeekDay
    this.byMonthDay = data.byMonthDay
    this.byYearDay = data.byYearDay
    this.prevRecurrenceAt = data.prevRecurrenceAt
    this.nextRecurrenceAt = data.nextRecurrenceAt
    this.currentCount = data.currentCount
    this.remainingCount = data.remainingCount
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
  }

  public get balance(): number {
    return toDollars(this.balanceCents)
  }

  public balanceLabel(currency: ExtendCurrency): React.ReactElement {
    return <Crncy currency={currency} amount={this.balance} />
  }

  public nextRecurrenceAtTimezonedMoment(timezone: string): Moment | undefined {
    return this.nextRecurrenceAt !== undefined
      ? convertUTCTimezone(timezone, this.nextRecurrenceAt)
      : undefined
  }

  public nextRecurrenceAtTimezonedString(timezone: string): string | undefined {
    const nextRecMoment = this.nextRecurrenceAtTimezonedMoment(timezone)
    return nextRecMoment !== undefined ? nextRecMoment.format('ll') : undefined
  }

  public prevRecurrenceAtTimezonedMoment(timezone: string): Moment | undefined {
    return this.prevRecurrenceAt !== undefined
      ? convertUTCTimezone(timezone, this.prevRecurrenceAt)
      : undefined
  }

  public prevRecurrenceAtTimezonedString(timezone: string): string | undefined {
    const prevRecMoment = this.prevRecurrenceAtTimezonedMoment(timezone)
    return prevRecMoment !== undefined ? prevRecMoment.format('ll') : undefined
  }

  public untilTimezonedMoment(timezone: string): Moment {
    return convertUTCTimezone(timezone, this.until).subtract(1, 'millisecond')
  }

  public createdAtTimezonedMoment(timezone: string): Moment | undefined {
    return this.createdAt !== undefined
      ? convertUTCTimezone(timezone, this.createdAt)
      : undefined
  }

  public createdAtTimezonedString(timezone: string): string | undefined {
    const createdAtMoment = this.createdAtTimezonedMoment(timezone)
    return createdAtMoment !== undefined
      ? createdAtMoment.format('ll')
      : undefined
  }

  public get periodTag(): string {
    // TODO: update the eslint package to take this buggy rule out
    switch (this.period) {
      case RecurrencePeriod.Daily: {
        return this.interval === 1 ? 'dailySingular' : 'dailyPlural'
      }
      case RecurrencePeriod.Weekly: {
        return this.interval === 1 ? 'weeklySingular' : 'weeklyPlural'
      }
      case RecurrencePeriod.Monthly: {
        return this.interval === 1 ? 'monthlySingular' : 'monthlyPlural'
      }
      case RecurrencePeriod.Yearly: {
        return 'yearly'
      }
    }
  }
}
